"use client";

import { Banknote, Lock } from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import {
	type ReactNode,
	useEffect,
	useRef,
	useState,
	useTransition,
} from "react";
import { MarkAsAppliedButton } from "@/components/applications/mark-as-applied-button";
import { SaveJobButton } from "@/components/feed/save-job-button";
import { PrepDiscoveryRail } from "@/components/hunt/prep-discovery-rail";
import {
	guestJobContext,
	type JobContextValue,
	JobContextValueProvider,
	useJobContext,
} from "@/components/jobs/job-context";
import { ZoneLink } from "@/components/layout/zone-link";
import { usePostHogReady } from "@/components/providers/posthog-provider";
import { PostApplyPushPrompt } from "@/components/push/post-apply-push-prompt";
import type { DestinationKind } from "@/lib/analytics/destination-class";
import { RECENT_APPLY_CLICK_KEY, useAnalytics } from "@/lib/analytics/hooks";
import {
	rememberRankContext,
	resolveRankContext,
} from "@/lib/analytics/rank-context";
import { trackJobInteraction } from "@/lib/analytics/track";
import { useSession } from "@/lib/auth/client";
import type { UserState } from "@/lib/entitlements";
import { reloadOncePerBuild } from "@/lib/error-recovery/reload-budget";
import type { Locale } from "@/lib/i18n/config";
import { isServerActionSkewError } from "@/lib/profile/server-action-skew";
import { upsertApplication } from "@/server/actions/applications";

// The job detail page renders statically (ISR) as the GUEST view so Cloudflare
// can edge-cache the HTML and Googlebot is served in ~50ms. The personalised
// bits — apply unlock, salary preview, applied state, ad suppression for Pro —
// hydrate client-side from /api/me/job-context. The cached HTML always shows
// the guest state; logged-in users see it flip after this provider resolves.

// Re-exported so existing call sites can keep importing from this module.
export { useJobContext };

export function JobContextProvider({
	jobId,
	locale,
	guestIsPreview,
	children,
}: {
	jobId: string;
	locale: string;
	/** The salary/apply preview state baked into the cached guest HTML. */
	guestIsPreview: boolean;
	children: ReactNode;
}) {
	const { data: session, isPending } = useSession();
	const isAuthenticated = !!session?.user;

	// Initial value MUST equal the server-rendered guest state to avoid a
	// hydration mismatch — `loading` is the only field that differs.
	const [value, setValue] = useState<JobContextValue>(() => ({
		...guestJobContext(guestIsPreview),
		loading: true,
	}));

	useEffect(() => {
		// Wait for better-auth to resolve the session before deciding.
		if (isPending) return;

		// Logged out → keep the guest state (identical to the cached HTML).
		if (!isAuthenticated) {
			setValue(guestJobContext(guestIsPreview));
			return;
		}

		let cancelled = false;
		void (async () => {
			try {
				const resp = await fetch(
					`/api/me/job-context?jobId=${encodeURIComponent(jobId)}&locale=${encodeURIComponent(locale)}`,
					{
						method: "GET",
						credentials: "include",
						cache: "no-store",
						headers: { Accept: "application/json" },
					},
				);
				if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
				const data = (await resp.json()) as Partial<JobContextValue>;
				if (cancelled) return;
				setValue({
					loading: false,
					isAuthenticated: true,
					userState: (data.userState as UserState) ?? "free",
					canApply: data.canApply ?? true,
					isPreview: data.isPreview ?? guestIsPreview,
					isAlreadyApplied: data.isAlreadyApplied ?? false,
					isSaved: data.isSaved ?? false,
					coachingEnabled: data.coachingEnabled ?? false,
					hasResume: data.hasResume ?? false,
					coachingSessionsThisMonth: data.coachingSessionsThisMonth ?? 0,
					matchTeaser: data.matchTeaser ?? null,
					fitScoreBreakdown: data.fitScoreBreakdown ?? null,
				});
			} catch {
				if (cancelled) return;
				// Network/5xx — assume an authenticated free user with apply
				// unlocked; keep whatever preview state the guest HTML showed so
				// we never reveal a Pro-gated salary on error. Coaching stays
				// fail-closed off — it's a nice-to-have, never worth a bad request.
				setValue({
					loading: false,
					isAuthenticated: true,
					userState: "free",
					canApply: true,
					isPreview: guestIsPreview,
					isAlreadyApplied: false,
					// Fail-safe FALSE on a network/5xx error: an unsaved job shown as
					// saved would swallow the user's click (the toggle would unsave),
					// which is worse than showing an already-saved job as unsaved.
					isSaved: false,
					coachingEnabled: false,
					hasResume: false,
					coachingSessionsThisMonth: 0,
					matchTeaser: null,
					fitScoreBreakdown: null,
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, isPending, jobId, locale, guestIsPreview]);

	return (
		<JobContextValueProvider value={value}>{children}</JobContextValueProvider>
	);
}

// ── Islands ────────────────────────────────────────────────────────────────

/** Renders the "Mark as applied" button only once we know the user is signed in. */
export function MarkAsAppliedSlot({
	jobId,
	jobTitle,
	locale,
	labelIdle,
	labelApplied,
	labelSaving,
	className,
	webPushEnabled,
}: {
	jobId: string;
	/** Job title — personalizes the post-apply coaching-offer copy/modal. */
	jobTitle: string;
	locale: Locale;
	labelIdle: string;
	labelApplied: string;
	labelSaving: string;
	className: string;
	/** Server-resolved `web-push` flag — enables the one-time post-apply push
	 * opt-in right after a fresh confirm on this, the primary apply CTA. */
	webPushEnabled?: boolean;
}) {
	const {
		isAuthenticated,
		isAlreadyApplied,
		coachingEnabled,
		hasResume,
		coachingSessionsThisMonth,
	} = useJobContext();
	if (!isAuthenticated) return null;
	return (
		<MarkAsAppliedButton
			jobId={jobId}
			jobTitle={jobTitle}
			locale={locale}
			initiallyApplied={isAlreadyApplied}
			coachingEnabled={coachingEnabled}
			hasResume={hasResume}
			coachingSessionsThisMonth={coachingSessionsThisMonth}
			labelIdle={labelIdle}
			labelApplied={labelApplied}
			labelSaving={labelSaving}
			className={className}
			webPushEnabled={webPushEnabled}
		/>
	);
}

/**
 * Prep discovery rail (PR-6) — surfaces the free mock-interview CTA grounded
 * in THIS role. Gated on `isAuthenticated` (from `useJobContext`) so the ISR
 * guest view renders nothing and `prep_rail_shown` never fires for anon
 * visitors / crawlers. The rail reuses `PrepDiscoveryRail` (shared with the
 * seeker dashboard) with `surface: "job_detail"`.
 */
export function JobDetailPrepRail({
	jobId,
	jobTitle,
	locale,
}: {
	jobId: string;
	jobTitle: string;
	locale: Locale;
}) {
	const { isAuthenticated, hasResume } = useJobContext();
	if (!isAuthenticated) return null;
	return (
		<PrepDiscoveryRail
			locale={locale}
			surface="job_detail"
			jobId={jobId}
			jobTitle={jobTitle}
			isAuthenticated={isAuthenticated}
			hasResume={hasResume}
		/>
	);
}

/**
 * Salary row in the metadata sidebar. Shows the real range when the user has
 * access, a blurred placeholder when previewed. Initial render matches the
 * cached guest HTML; flips after the overlay resolves.
 */
export function JobSalaryField({
	payRange,
	estPayRange,
	hasPay,
	label,
	estimateLabel,
	estimateNote,
	indexable,
}: {
	payRange: string | null;
	/** Research-grounded estimate — only used when payRange is null. */
	estPayRange?: string | null;
	hasPay: boolean;
	label: string;
	estimateLabel?: string;
	estimateNote?: string;
	/**
	 * `jobIsIndexable` from the page, threaded straight through as a prop (same
	 * pattern as `AdSlot`'s `indexable` a few lines up in page.tsx) — a page
	 * property, not a per-user one, so it does not belong in the fetched
	 * `JobContextValue` overlay.
	 *
	 * When true, the FAQ answer and the JobPosting JSON-LD on this SAME page
	 * already publish `payRange` in plain text/markup (`buildJobFaq` /
	 * `buildJobPostingSchema`, both gated by the identical `jobIsIndexable`
	 * predicate) — so blurring it here protects nothing and only reads as the
	 * site hiding something it has already shown. The preview gate still
	 * applies on a non-indexable page (thin/expired/noindexed), where nothing
	 * else on the page has published the number either.
	 */
	indexable: boolean;
}) {
	const { isPreview: contextIsPreview } = useJobContext();
	const isPreview = contextIsPreview && !indexable;

	if (!isPreview && payRange) {
		return (
			<div className="flex gap-3">
				<Banknote className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
				<div>
					<div className="font-medium">{label}</div>
					<div className="font-medium text-success dark:text-emerald-400">
						{payRange}
					</div>
				</div>
			</div>
		);
	}

	if (!isPreview && !payRange && estPayRange) {
		return (
			<div className="flex gap-3">
				<Banknote className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
				<div>
					<div className="font-medium">{estimateLabel ?? label}</div>
					<div className="font-medium text-success dark:text-emerald-400">
						{estPayRange}
					</div>
					{estimateNote && (
						<div className="text-xs text-muted-foreground">{estimateNote}</div>
					)}
				</div>
			</div>
		);
	}

	if (isPreview && (hasPay || estPayRange)) {
		return (
			<div className="flex gap-3">
				<Banknote className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
				<div>
					<div className="font-medium">{label}</div>
					<div
						aria-hidden="true"
						data-a11y-decorative-blur=""
						className="select-none font-medium text-muted-foreground/40 blur-[4px]"
					>
						$XX,XXX
					</div>
				</div>
			</div>
		);
	}

	return null;
}

// Bilingual copy for the anonymous match-score + salary teaser
// (`growth-jobdetail-match-teaser`, R11). Reserved height (`min-h-*` on the
// outer card below) is IDENTICAL across every branch — blurred, full score,
// and no-signal — so the client-side flip from the cached guest HTML to a
// signed-in user's real state never shifts layout (zero CLS regression).
const JOB_MATCH_TEASER_COPY = {
	en: {
		heading: "Your match score",
		blurredSubline:
			"Free — see how your skills, salary target, and location line up.",
		signInCta: "Sign in to see your match",
		strongTier: "Strong match",
		partialTier: "Partial match",
		noSignalBody:
			"Answer a couple of quick questions in Hunt to unlock your real match score for this role.",
		noSignalCta: "Build your match profile",
		signalCaption: (n: number) =>
			`Based on ${n} signal${n === 1 ? "" : "s"} you've given Hunt`,
	},
	id: {
		heading: "Skor kecocokanmu",
		blurredSubline:
			"Gratis — lihat kecocokan skill, target gaji, dan lokasimu.",
		signInCta: "Masuk untuk lihat kecocokanmu",
		strongTier: "Kecocokan kuat",
		partialTier: "Kecocokan sebagian",
		noSignalBody:
			"Jawab beberapa pertanyaan cepat di Hunt untuk buka skor kecocokan asli buat lowongan ini.",
		noSignalCta: "Lengkapi profil kecocokanmu",
		signalCaption: (n: number) =>
			`Berdasarkan ${n} sinyal yang kamu berikan ke Hunt`,
	},
} as const;

/**
 * Job-detail "match score + salary" teaser (R11) — reuses the existing
 * `scoreJob` output (via `/api/me/job-context`'s `matchTeaser` field, see
 * `@/lib/jobs/match-teaser`) rather than fabricating a score client-side.
 *
 * Anonymous visitors see a blurred score + salary hint with a "sign in to
 * see your match" CTA (the ~21k/mo job-detail renders that currently give a
 * guest no reason to create an account). Signed-in users with enough Hunt
 * signal see the real score; signed-in users without enough signal yet see
 * an honest prompt to Hunt instead of a fabricated number — same rule the
 * Approval Card follows (`PERSONALIZED_SIGNAL_THRESHOLD`).
 *
 * `enabled` is the server-resolved `growth-jobdetail-match-teaser` flag
 * (baked once per ISR revalidation, see `@/lib/jobs/match-teaser-flag`) —
 * when false the component renders nothing, and since that decision is
 * identical for every visitor within a cache window it never toggles
 * mid-session, so it can never itself cause a layout shift.
 */
export function JobMatchTeaser({
	jobId,
	locale,
	jobPathname,
	enabled,
}: {
	jobId: string;
	locale: Locale;
	/** Locale-correct, slug-bearing detail path — round-trips signup back here. */
	jobPathname: string;
	enabled: boolean;
}) {
	const { isAuthenticated, matchTeaser } = useJobContext();
	const { trackGateImpression, trackGateCtaClick } = useAnalytics();
	const impressionFired = useRef(false);

	useEffect(() => {
		if (!enabled || isAuthenticated || impressionFired.current) return;
		impressionFired.current = true;
		trackGateImpression({
			surface_id: "match_teaser_gate",
			gate_type: "signup",
			trigger: "view_detail",
			job_id: jobId,
		});
	}, [enabled, isAuthenticated, jobId, trackGateImpression]);

	if (!enabled) return null;

	const copy = JOB_MATCH_TEASER_COPY[locale === "id" ? "id" : "en"];
	const signupHref = `/${locale}/signup?callbackURL=${encodeURIComponent(jobPathname)}`;

	const onSignupClick = () => {
		trackGateCtaClick({
			surface_id: "match_teaser_gate",
			gate_type: "signup",
			trigger: "view_detail",
			job_id: jobId,
			cta_label: copy.signInCta,
		});
	};

	return (
		<div
			data-testid="job-match-teaser"
			className="min-h-[176px] rounded-xl border border-border/60 bg-card p-5"
		>
			<div className="mb-3 flex items-center gap-2">
				<span className="font-medium text-foreground">{copy.heading}</span>
			</div>

			{!isAuthenticated ? (
				<div
					data-testid="job-match-teaser-blurred"
					className="flex flex-col gap-3"
				>
					<div className="flex items-baseline gap-2">
						<span
							aria-hidden="true"
							data-a11y-decorative-blur=""
							className="select-none text-3xl font-bold text-muted-foreground/40 blur-[4px]"
						>
							82%
						</span>
						<span
							aria-hidden="true"
							data-a11y-decorative-blur=""
							className="select-none text-sm text-muted-foreground/40 blur-[4px]"
						>
							$4,200–$5,800/mo
						</span>
					</div>
					<p className="text-xs text-muted-foreground">{copy.blurredSubline}</p>
					<Link
						href={signupHref}
						onClick={onSignupClick}
						className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						<Lock className="h-3.5 w-3.5" aria-hidden="true" />
						{copy.signInCta}
					</Link>
				</div>
			) : matchTeaser ? (
				<div
					data-testid="job-match-teaser-full"
					className="flex flex-col gap-2"
				>
					<div className="flex items-baseline gap-2">
						<span className="text-3xl font-bold text-foreground">
							{matchTeaser.score}%
						</span>
						<span
							className={
								matchTeaser.tier === "strong"
									? "rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-success dark:text-emerald-400"
									: "rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400"
							}
						>
							{matchTeaser.tier === "strong"
								? copy.strongTier
								: copy.partialTier}
						</span>
					</div>
					{matchTeaser.salaryHint && (
						<p className="text-sm font-medium text-success dark:text-emerald-400">
							{matchTeaser.salaryHint}
						</p>
					)}
					<p className="text-xs text-muted-foreground">
						{copy.signalCaption(matchTeaser.signalCount)}
					</p>
				</div>
			) : (
				<div
					data-testid="job-match-teaser-nosignal"
					className="flex flex-col gap-3"
				>
					<p className="text-sm text-muted-foreground">{copy.noSignalBody}</p>
					<ZoneLink
						href={`/${locale}/seeker`}
						className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{copy.noSignalCta}
					</ZoneLink>
				</div>
			)}
		</div>
	);
}

/** Suppresses an ad slot for Pro users (who paid for an ad-free experience). */
export function JobDetailAd({ children }: { children: ReactNode }) {
	const { userState } = useJobContext();
	if (userState === "pro") return null;
	return <>{children}</>;
}

/**
 * Fires the `job_detail_rendered` PostHog event client-side, so the browser's
 * real distinct_id joins the funnel (the old server event used job.id as a
 * fallback distinct_id, breaking gate→signup attribution).
 *
 * This emit is intentionally client-only. It was moved server→client on
 * 2026-05-30 (commit a901d261a, the ISR conversion) — the server emit fired
 * on every render (crawlers, no-JS, non-consent) and inflated the "88% apply
 * rate" illusion. The ~95% volume drop that day was the loss of that
 * crawler/no-JS traffic, NOT a regression. Do not re-add a server emit
 * here; for any top-of-funnel job-detail volume, filter `$pageview` by
 * `properties.$pathname LIKE '/%/jobs/%'`. The event is kept alive only for
 * its `summary_generation_model` prop (model-quality rollup).
 */
export function JobViewTracker({
	jobId,
	locale,
	usedFallback,
	hasVisibleContent,
	completeness,
	summaryGenerationModel,
	sourceSlug = null,
	geoFit = null,
}: {
	jobId: string;
	locale: string;
	usedFallback: boolean;
	hasVisibleContent: boolean;
	completeness: number;
	summaryGenerationModel: string | null;
	/** `sources.slug` — the `source_id` rank-context prop. */
	sourceSlug?: string | null;
	/** Stored `jobs.geo_fit` — the `indonesia_eligibility` rank-context prop. */
	geoFit?: string | null;
}) {
	const posthog = usePostHog();
	const isReady = usePostHogReady();
	const fired = useRef(false);

	// Wait for the provider's ready flag. `usePostHog()` returns the singleton
	// before `posthog.init` runs (init waits on the async privacy-region lookup),
	// and posthog-js drops a capture made before init. Without this gate the
	// event fired only when the region resolved before this effect, so from
	// 2026-10-07 it reached ~15% of mobile job-detail pageviews (34% before).
	useEffect(() => {
		if (!isReady || fired.current) return;
		fired.current = true;
		// Rank context (PostHog audit 2026-10-21): position / list_surface /
		// score / algo_version come from the list click that opened this page
		// (stashed in sessionStorage by `trackJobClick`); null on a direct
		// landing. source_id / indonesia_eligibility are this job's own facts,
		// stashed back so the apply CTA on this page reports them too.
		const rank = resolveRankContext(jobId, {
			source_id: sourceSlug,
			indonesia_eligibility: geoFit,
		});
		rememberRankContext(jobId, {
			source_id: rank.source_id,
			indonesia_eligibility: rank.indonesia_eligibility,
		});
		posthog?.capture("job_detail_rendered", {
			locale,
			used_fallback: usedFallback,
			has_visible_content: hasVisibleContent,
			completeness,
			job_id: jobId,
			summary_generation_model: summaryGenerationModel,
			...rank,
		});
	}, [
		isReady,
		posthog,
		jobId,
		locale,
		usedFallback,
		hasVisibleContent,
		completeness,
		summaryGenerationModel,
		sourceSlug,
		geoFit,
	]);

	return null;
}

/**
 * Fires `job_detail_scroll_depth` at 25 / 50 / 75 / 100 % thresholds.
 * Each threshold fires at most once per component mount.
 */
export function ScrollDepthTracker({ jobId }: { jobId: string }) {
	const posthog = usePostHog();
	const fired = useRef(new Set<number>());

	useEffect(() => {
		const thresholds = [25, 50, 75, 100] as const;
		const check = () => {
			const el = document.documentElement;
			const depth = Math.round(
				((el.scrollTop + el.clientHeight) / el.scrollHeight) * 100,
			);
			for (const t of thresholds) {
				if (depth >= t && !fired.current.has(t)) {
					fired.current.add(t);
					posthog?.capture("job_detail_scroll_depth", {
						job_id: jobId,
						depth_pct: t,
					});
				}
			}
		};
		window.addEventListener("scroll", check, { passive: true });
		check(); // capture depth if page is already partially scrolled on mount
		return () => window.removeEventListener("scroll", check);
	}, [posthog, jobId]);

	return null;
}

/**
 * Persists ONE `view_engagement` D1 interaction on pagehide/unmount, carrying
 * `{ dwell_ms, max_scroll_pct }` — the read-quality signal `INTERACTION_WEIGHTS`
 * (`behavioral-signals.ts`) and the nightly affinity cron (`compute-feed-affinity`
 * via `getInteractionAggregates`) use to weight an engaged read above a 5s
 * bounce. Additive: never mutates/replaces the flat `view` row.
 *
 * Mirrors `FeedScrollTracker`'s dwell/scroll-tracking + pagehide-flush pattern
 * (`src/components/feed/feed-scroll-tracker.tsx`) so this D1 beacon fires
 * reliably even on a hard tab close, not just component unmount. `trackJobInteraction`
 * already sends via `fetch(..., { keepalive: true })` (see `src/lib/analytics/track.ts`),
 * so the write survives the navigation that triggers `pagehide`.
 */
export function JobEngagementBeacon({
	jobId,
	locale,
}: {
	jobId: string;
	locale: string;
}) {
	useEffect(() => {
		const startedAt = Date.now();
		let maxScrollPct = 0;
		let activeMs = 0;
		let lastTick = startedAt;
		let visible = !document.hidden;
		let sent = false;

		const onScroll = () => {
			const el = document.documentElement;
			const scrollable = el.scrollHeight - el.clientHeight;
			if (scrollable <= 0) {
				maxScrollPct = Math.max(maxScrollPct, 100);
				return;
			}
			const pct = ((el.scrollTop + el.clientHeight) / el.scrollHeight) * 100;
			if (pct > maxScrollPct) maxScrollPct = Math.min(100, pct);
		};

		const tick = () => {
			const now = Date.now();
			if (visible) activeMs += now - lastTick;
			lastTick = now;
		};

		const onVisibility = () => {
			tick();
			visible = !document.hidden;
		};

		const flush = () => {
			if (sent) return;
			tick();
			sent = true;
			trackJobInteraction({
				jobId,
				interactionType: "view_engagement",
				locale,
				metadata: {
					dwell_ms: activeMs,
					max_scroll_pct: Number(maxScrollPct.toFixed(1)),
				},
			});
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("pagehide", flush);

		const tickInterval = window.setInterval(tick, 5_000);

		return () => {
			window.removeEventListener("scroll", onScroll);
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("pagehide", flush);
			window.clearInterval(tickInterval);
			flush();
		};
	}, [jobId, locale]);

	return null;
}

/** How recent an apply click must be to count as "still actively applying"
 * for the value-moment below — long enough to survive browsing a few more
 * listings in the same sitting, short enough that it's not stale intent. */
const RECENT_APPLY_CLICK_WINDOW_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Push-opt-in value moment (retention R8): a seeker who recently clicked an
 * outbound apply link and is now browsing another job is showing real
 * recurring intent, so it's a good moment to offer push as a lower-friction
 * daily-loop channel than email.
 *
 * Re-keyed (telemetry-gap-remediation 2026-07-21) off `trackApplyClick`'s
 * `RECENT_APPLY_CLICK_KEY` localStorage marker (a real signal — 614
 * `job_apply_outbound_clicked`/30d) instead of an `utm_source=email` landing
 * count. The email-click heuristic never fired live: this repo's telemetry
 * shows email-link clicks are ~100% bot/scanner traffic (GET-request
 * pre-fetchers), not real humans re-visiting from an inbox. The marker is
 * consumed (cleared) once shown so it doesn't re-fire on every subsequent
 * job view until the next real apply click resets it.
 *
 * Read client-side only (`window.localStorage` in an effect, never server
 * `searchParams`) because the job-detail page is ISR-cached as the guest
 * view — nothing per-visitor may influence what gets baked into the cached
 * HTML (see `src/app/CLAUDE.md`).
 *
 * Gated on `isAuthenticated` (from `useJobContext()`) since push requires a
 * signed-in user.
 */
export function RecentApplyClickPushPrompt({
	locale,
	webPushEnabled,
}: {
	locale: Locale;
	webPushEnabled: boolean;
}) {
	const { isAuthenticated, loading } = useJobContext();
	const [shouldPrompt, setShouldPrompt] = useState(false);
	const checkedRef = useRef(false);

	useEffect(() => {
		if (!webPushEnabled || loading || !isAuthenticated || checkedRef.current) {
			return;
		}
		checkedRef.current = true;
		try {
			const raw = window.localStorage.getItem(RECENT_APPLY_CLICK_KEY);
			if (!raw) return;
			const clickedAtMs = Number.parseInt(raw, 10);
			if (!Number.isFinite(clickedAtMs)) return;
			const withinWindow =
				Date.now() - clickedAtMs < RECENT_APPLY_CLICK_WINDOW_MS;
			// Consume the marker either way — a stale/expired marker should stop
			// being re-checked on every future job view too, not just a fresh one.
			window.localStorage.removeItem(RECENT_APPLY_CLICK_KEY);
			if (withinWindow) setShouldPrompt(true);
		} catch {
			// localStorage unavailable (private mode) — skip silently, never block render.
		}
	}, [webPushEnabled, loading, isAuthenticated]);

	if (!shouldPrompt) return null;
	return (
		<PostApplyPushPrompt
			locale={locale}
			source="recent_apply_click"
			variant="engaged"
		/>
	);
}

// Bilingual copy for the "Save to pipeline" CTA below, keyed by button state.
// Same lookup-table shape as `JOB_MATCH_TEASER_COPY` above — avoids a nested
// ternary in the render for the (locale × state) product.
const SAVE_TO_PIPELINE_COPY = {
	en: {
		idle: "Save to pipeline",
		saving: "Saving…",
		saved: "Saved to pipeline ✓",
		error: "Try again",
	},
	id: {
		idle: "Simpan ke pipeline",
		saving: "Menyimpan…",
		saved: "Tersimpan di pipeline ✓",
		error: "Coba lagi",
	},
} as const;

/**
 * The job detail page's save control.
 *
 * Until this shipped there was NO general save affordance here at all. Every
 * feed card has one (`job-row.tsx`), but the detail page — the highest-intent
 * surface on the site, reached by deliberately clicking a job — offered saving
 * only through `SaveToPipelineCta` below, which additionally requires the
 * `retention-brief-landing-save` flag AND an email-sourced landing. So a
 * visitor who arrived from search or the feed, read the whole posting and
 * wanted to come back to it, had nowhere to put it. `job_saved` fired ONCE in
 * the 30 days to 2026-08-27.
 *
 * An island, not a server-rendered button, because this page is ISR-cached as
 * the guest view and must stay per-user-free (see `src/app/CLAUDE.md`). Saved
 * state rides `useJobContext()`, whose `/api/me/job-context` fetch already
 * looked up this job's tracker row for `isAlreadyApplied` — so the control
 * costs no extra request.
 *
 * Renders nothing until the context resolves, so the button never flips from
 * unsaved to saved under the user's cursor, and nothing for guests — matching
 * `SaveJobButton`'s own guest behaviour, which redirects to `/login`.
 */
export function SaveJobCta({
	jobId,
	locale,
}: {
	jobId: string;
	locale: string;
}) {
	const { loading, isAuthenticated, isSaved } = useJobContext();
	if (loading || !isAuthenticated) return null;
	return (
		<SaveJobButton
			jobId={jobId}
			initialIsSaved={isSaved ?? false}
			isAuthenticated={isAuthenticated}
			canSaveJobs
			locale={locale}
			showLabel
			className="w-full justify-center"
		/>
	);
}

/**
 * "Save to pipeline" CTA for sessions landing from a daily-brief email
 * (retention R9). Most brief clicks today end at job_card -> job detail ->
 * outbound click -> gone; this gives an email-landed visitor an in-app
 * next-action that deepens the loop instead of just ending it off-site.
 *
 * Detects an email-click landing via the `utm_source=email` query param that
 * `applyEmailUtm` (`src/lib/email/utm.ts`) stamps on every outbound email
 * link. Read
 * client-side only (`window.location.search`, not `searchParams`) because
 * the job-detail page is ISR-cached as the guest view — the query string
 * must never influence what gets baked into the cached HTML (see
 * `src/app/CLAUDE.md`).
 *
 * Reuses R6's `upsertApplication` mutation — the same one `MarkAsAppliedButton`
 * and the "did you apply?" follow-up prompts call — to write a `saved`-status
 * `application_tracker` row. No parallel tracking mechanism.
 *
 * Gated on:
 *  - the server-resolved `retention-brief-landing-save` flag (`enabled`,
 *    baked once per ISR revalidation — see `RETENTION_BRIEF_LANDING_SAVE_FLAG`
 *    in `src/lib/db/queries/applications.ts`),
 *  - `isAuthenticated` (writing a tracker row needs a session — anonymous
 *    visitors get no CTA, matching `SaveJobButton`'s guest behavior),
 *  - `!isAlreadyApplied`, so a job already past `saved` (any status other
 *    than `saved` — a `clicked` outbound hop, an `applied` self-report, …;
 *    see `isAlreadyApplied`'s definition in `/api/me/job-context/route.ts`)
 *    never gets silently downgraded back to `saved`.
 *
 * Renders alongside — never instead of — the existing Apply CTA and
 * `MarkAsAppliedSlot`; this is purely additive.
 */
export function SaveToPipelineCta({
	jobId,
	locale,
	enabled,
}: {
	jobId: string;
	locale: Locale;
	/** Server-resolved `retention-brief-landing-save` flag. Fails closed. */
	enabled: boolean;
}) {
	const { isAuthenticated, isAlreadyApplied, loading } = useJobContext();
	const [isEmailLanding, setIsEmailLanding] = useState(false);
	const [state, setState] = useState<"idle" | "saving" | "saved" | "error">(
		"idle",
	);
	const [, startTransition] = useTransition();
	const posthog = usePostHog();
	const checkedRef = useRef(false);

	useEffect(() => {
		if (!enabled || checkedRef.current) return;
		checkedRef.current = true;
		try {
			const params = new URLSearchParams(window.location.search);
			if (params.get("utm_source") === "email") setIsEmailLanding(true);
		} catch {
			// URLSearchParams unavailable — skip silently, never block render.
		}
	}, [enabled]);

	if (
		!enabled ||
		!isEmailLanding ||
		loading ||
		!isAuthenticated ||
		isAlreadyApplied
	) {
		return null;
	}

	const copy = SAVE_TO_PIPELINE_COPY[locale === "id" ? "id" : "en"];

	const handleClick = () => {
		if (state === "saving" || state === "saved") return;
		posthog?.capture("brief_landing_save_to_pipeline_clicked", {
			job_id: jobId,
		});
		startTransition(async () => {
			setState("saving");
			try {
				const res = await upsertApplication(jobId, "saved");
				if ("error" in res) {
					setState("idle");
					return;
				}
				setState("saved");
			} catch (error) {
				if (isServerActionSkewError(error) && reloadOncePerBuild("action")) {
					return;
				}
				setState("error");
			}
		});
	};

	return (
		<button
			type="button"
			disabled={state === "saving" || state === "saved"}
			onClick={handleClick}
			aria-pressed={state === "saved"}
			className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-default disabled:opacity-80"
		>
			{copy[state]}
		</button>
	);
}

/**
 * Zero-size sentinel placed just above the apply button. Fires
 * `apply_button_visible` via IntersectionObserver the first time the
 * sentinel enters the viewport. De-duped per job per browser session via
 * sessionStorage so back-navigation doesn't double-count.
 *
 * `footer` mode covers the mobile fixed-footer CTA, which is visible from first
 * paint on small screens (`lg:hidden`, `position: fixed`). An IntersectionObserver
 * on a normal-flow sentinel can't represent a fixed element — it would sit at the
 * document end and only fire after a full scroll — so the footer instance fires
 * once on mount when the viewport is below `lg`. Both instances share the same
 * sessionStorage key, so whichever sees the CTA first wins and there's no
 * double-count. Without this, mobile users (who apply via the always-visible
 * footer, not the buried sidebar) never fired the event, leaving
 * `apply_button_visible` < `job_apply_outbound_clicked`.
 */
export function ApplyButtonSentinel({
	jobId,
	footer = false,
	destinationKind = null,
	destinationLabel = null,
}: {
	jobId: string;
	footer?: boolean;
	/** `describeApplyDestination()` result — same values the CTA label shows. */
	destinationKind?: DestinationKind | null;
	destinationLabel?: string | null;
}) {
	const posthog = usePostHog();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const key = `apply_visible_${jobId}`;
		let alreadySeen = false;
		try {
			alreadySeen = !!sessionStorage.getItem(key);
		} catch {
			// sessionStorage unavailable (SecurityError, private mode) — treat as unseen.
		}
		if (alreadySeen) return;
		const fire = () => {
			posthog?.capture("apply_button_visible", {
				job_id: jobId,
				destination_kind: destinationKind,
				destination_label: destinationLabel,
			});
			try {
				sessionStorage.setItem(key, "1");
			} catch {
				// best-effort de-dupe only — a write failure must never block the capture.
			}
		};

		// Footer variant: the mobile fixed footer is on screen at first paint
		// below the lg breakpoint. On desktop it's `display:none`, so the sidebar
		// sentinel (below) owns the event instead.
		if (footer) {
			if (window.matchMedia("(max-width: 1023.98px)").matches) fire();
			return;
		}

		const el = ref.current;
		if (!el) return;
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					fire();
					obs.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [posthog, jobId, footer, destinationKind, destinationLabel]);

	return footer ? null : <div ref={ref} aria-hidden="true" />;
}

/**
 * Instruments the collapsed "Original description" `<details>` as a three-step
 * funnel so we can decide whether the accordion earns its place, should be
 * downgraded to a plain inline source link, or dropped — per source:
 *   1. `original_description_visible`   — the section scrolled into view (reach,
 *      the true denominator; scroll-depth % is only a coarse proxy).
 *   2. `original_description_expanded`  — the user opened it (intent to read).
 *   3. `original_source_link_clicked`   — the user clicked through to the source
 *      (the payoff: expanding actually led somewhere).
 * Each fires at most once per mount. The `<details>` is server-rendered
 * (cacheable HTML); this is a zero-size island dropped inside it that wires the
 * native `toggle`, an IntersectionObserver, and a delegated click on the parent.
 */
export function DetailsOpenTracker({
	jobId,
	source,
}: {
	jobId: string;
	/** Ingestion source slug (adzuna, remoteok, wwr, …) — lets us segment which
	 * sources drive "check the original" behavior, and decide per-source whether
	 * the accordion earns its place vs a plain inline source link. */
	source: string;
}) {
	const posthog = usePostHog();
	const ref = useRef<HTMLSpanElement>(null);
	const expandedFired = useRef(false);
	const visibleFired = useRef(false);
	const clickFired = useRef(false);

	useEffect(() => {
		const details = ref.current?.closest("details");
		if (!details) return;
		const props = { job_id: jobId, source };

		const onToggle = () => {
			if (details.open && !expandedFired.current) {
				expandedFired.current = true;
				posthog?.capture("original_description_expanded", props);
			}
		};

		// Click-through to the source. The only anchor inside the panel is the
		// source attribution link (the raw description renders as plain text), so
		// a delegated click that resolves to any `<a>` is the source-link click.
		const onClick = (e: Event) => {
			if (clickFired.current) return;
			const target = e.target as Element | null;
			if (target?.closest("a")) {
				clickFired.current = true;
				posthog?.capture("original_source_link_clicked", props);
			}
		};

		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && !visibleFired.current) {
					visibleFired.current = true;
					posthog?.capture("original_description_visible", props);
					obs.disconnect();
				}
			},
			{ threshold: 0.5 },
		);

		details.addEventListener("toggle", onToggle);
		details.addEventListener("click", onClick);
		obs.observe(details);
		return () => {
			details.removeEventListener("toggle", onToggle);
			details.removeEventListener("click", onClick);
			obs.disconnect();
		};
	}, [posthog, jobId, source]);

	return <span ref={ref} className="hidden" aria-hidden="true" />;
}

/**
 * Source attribution in the job metadata panel ("Source: Ashby Job Boards").
 * Session replay (PR-6, ux-seeker-journey-remediation) showed this registering
 * as a confirmed dead click — it reads like a link (label styling next to
 * "Company", which IS one) but was plain text. Fires the SAME
 * `original_source_link_clicked` event, with the same `{ job_id, source }`
 * shape, as the delegated click inside the collapsed original-description
 * accordion above (`DetailsOpenTracker`) — one event name for "clicked
 * through to the source" regardless of which surface on the page it happened
 * on. Falls back to plain text (no link styling) when there's no real URL to
 * send the seeker to, so it stops looking clickable instead of dead-clicking.
 */
export function SourceAttributionLink({
	jobId,
	source,
	sourceUrl,
	sourceName,
}: {
	jobId: string;
	/** Same segmentation key as `DetailsOpenTracker`: slug when known, else name. */
	source: string;
	sourceUrl: string | null | undefined;
	sourceName: string;
}) {
	const posthog = usePostHog();
	const fired = useRef(false);

	if (!sourceUrl || !/^https?:\/\//.test(sourceUrl)) {
		return <span className="text-muted-foreground">{sourceName}</span>;
	}

	const onClick = () => {
		if (fired.current) return;
		fired.current = true;
		posthog?.capture("original_source_link_clicked", {
			job_id: jobId,
			source,
		});
	};

	return (
		<a
			href={sourceUrl}
			target="_blank"
			rel="nofollow noopener noreferrer"
			onClick={onClick}
			className="text-muted-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
		>
			{sourceName}
		</a>
	);
}
